// components/user/lead/CancelDrawer.jsx
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Drawer, Form, Select, Input, Button, Spin } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import { cancelServiceRequest, fetchCancelRescheduleReasons } from "../../../store/slices/serviceRequestSlice";
import { IS_DEVELOPMENT } from "../../../utils/constant";

const { Option } = Select;
const { TextArea } = Input;

const CancelDrawer = ({
  visible,
  order,
  onClose,
  onSuccess,
  isDarkMode,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [form] = Form.useForm();

  // Fetch cancel reasons when drawer opens
  useEffect(() => {
    if (visible) {
      loadReasons();
    }
  }, [visible]);

  const loadReasons = async () => {
    try {
      setReasonsLoading(true);
      const result = await dispatch(fetchCancelRescheduleReasons({ type: 'requested_to_cancel' })).unwrap();
      setReasons(result || []);
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error("Failed to load cancel reasons:", error);
      }
    } finally {
      setReasonsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (!values.reasonId) {
        toast.error("Please select a cancel reason");
        return;
      }
      
      setLoading(true);

      const payload = {
        reasonId: values.reasonId,
        remark: values.remark,
      };

      const result = await dispatch(
        cancelServiceRequest({
          serviceId: order.service_id,
          payload,
        }),
      ).unwrap();

      if (result) {
        toast.success("Service cancelled successfully");
        form.resetFields();
        onClose();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error("Cancel failed:", error);
      }
      toast.error(error || "Failed to cancel service");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title={
        <div className="flex items-center gap-2">
          <ExclamationCircleOutlined className="text-base text-red-500" />
          <span className="text-sm font-semibold">
            Cancel Service - {order?.service_no}
          </span>
        </div>
      }
      placement="right"
      onClose={handleClose}
      open={visible}
      width={400}
      closable={false}
      footer={null}
      className="cancel-drawer"
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto">
          <div
            className={`p-3 mb-4 rounded-lg ${isDarkMode ? "bg-gray-800" : "bg-gray-50"}`}
          >
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-500">Product:</span>
                <div className="font-medium">
                  {order?.variation?.title || "N/A"}
                </div>
              </div>
              <div>
                <span className="text-gray-500">Amount:</span>
                <div className="font-medium">
                  ₹
                  {parseFloat(order?.latestEvaluation?.evaluated_amount || 0).toLocaleString(
                    "en-IN",
                  )}
                </div>
              </div>
            </div>
          </div>

          <Form form={form} layout="vertical" size="middle">
            <Form.Item
              name="reasonId"
              label="Cancel Reason"
              rules={[{ required: true, message: "Please select a reason" }]}
              className="mb-3"
            >
              <Select
                placeholder="Select reason"
                className="w-full"
                allowClear={false}
                style={{ height: "36px" }}
                loading={reasonsLoading}
                notFoundContent={reasonsLoading ? <Spin size="small" /> : "No reasons available"}
              >
                {reasons.map((reason) => (
                  <Option key={reason.id} value={reason.id}>
                    {reason.message}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="remark" label="Remark" className="mb-3">
              <TextArea
                rows={2}
                placeholder="Optional remarks"
                className="text-sm"
                style={{ padding: "8px 12px" }}
              />
            </Form.Item>
          </Form>
        </div>

        <div
          className={`pt-3 mt-2 border-t ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}
        >
          <div className="flex gap-2">
            <Button
              onClick={handleClose}
              disabled={loading}
              className="flex-1 text-sm rounded-md h-9"
            >
              Back
            </Button>
            <Button
              danger
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-1 text-sm rounded-md h-9"
            >
              {loading ? "Cancelling..." : "Confirm Cancel"}
            </Button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .cancel-drawer :global(.ant-select-selector) {
          padding: 4px 11px !important;
          height: 36px !important;
        }
        .cancel-drawer :global(.ant-input) {
          padding: 8px 12px !important;
        }
        .cancel-drawer :global(.ant-input-affix-wrapper) {
          padding: 8px 12px !important;
        }
      `}</style>
    </Drawer>
  );
};

export default CancelDrawer;